import { z } from "zod";
export const APP_NAME = "Passa a Página";
// --- auth ---
export const requestLinkSchema = z.object({
    email: z.string().email("Email inválido").max(320),
    ageConsent: z.literal(true, {
        errorMap: () => ({ message: "Tens de confirmar a idade ou autorização" }),
    }),
});
export const sessionUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: z.enum(["user", "admin"]),
    points: z.number().int(),
    // Optional/nullable — the public signup never collects a name, so most
    // users have none until they give one at checkout or an admin records it.
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
});
export const meSchema = z.object({
    user: sessionUserSchema.nullable(),
});
// --- books ---
export const bookConditionSchema = z.enum(["mint", "good", "fair", "worn"]);
export const bookStatusSchema = z.enum(["available", "on_hold", "checked_out"]);
// ISBN-10 (9 digits + a check digit that may be 0–9 or the letter X) or
// ISBN-13 (13 digits), evaluated after separators (hyphens/spaces/dots) are
// stripped. Old books often carry only an ISBN-10, whose final check digit
// can legitimately be "X" (= 10). Use after cleaning with cleanIsbnDigits or
// an equivalent strip; the check is case-insensitive on the X.
export const ISBN_RE = /^(?:\d{9}[\dX]|\d{13})$/;
export function isValidIsbn(cleaned) {
    return ISBN_RE.test(cleaned.toUpperCase());
}
export const bookSchema = z.object({
    id: z.string().uuid(),
    isbn: z.string().nullable(),
    title: z.string(),
    author: z.string().nullable(),
    coverUrl: z.string().nullable(),
    description: z.string().nullable(),
    genre: z.string().nullable(),
    condition: bookConditionSchema,
    status: bookStatusSchema,
});
export const browseQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    genre: z.string().trim().max(120).optional(),
    author: z.string().trim().max(500).optional(),
    status: bookStatusSchema.optional(),
    sort: z.enum(["newest", "oldest", "title", "author"]).default("newest"),
    page: z.coerce.number().int().positive().max(1000).default(1),
    limit: z.coerce.number().int().positive().max(60).default(24),
});
export const browseResponseSchema = z.object({
    items: z.array(bookSchema),
    total: z.number().int(),
    page: z.number().int(),
    pages: z.number().int(),
    facets: z.object({
        genres: z.array(z.string()),
        authors: z.array(z.string()),
    }),
});
// --- donations ---
export const donationTypeSchema = z.enum(["books", "boxes", "bags"]);
export const submitDonationSchema = z.object({
    email: z.string().email().max(320),
    type: donationTypeSchema,
    quantity: z.number().int().positive().max(10000),
    message: z.string().trim().max(2000).optional().nullable(),
});
// --- admin scan-in ---
// Either ISBN or title is required. ISBN flow uses Google Books to fill
// metadata; title flow stores what the admin typed and the server
// generates a placeholder ISBN so the column always has a value.
//
// noCredit = true: anonymous donor path. No email required, no point
// credited, no users row touched. Book is still recorded with donorEmail
// left null.
export const scanInSchema = z
    .object({
    isbn: z.string().trim().min(10).max(32).optional(),
    title: z.string().trim().min(1).max(500).optional(),
    author: z.string().trim().max(500).optional(),
    // Optional cached metadata to short-circuit Google Books. Only used when
    // title is also present (manual-data path). Lets the local-cache lookup
    // forward genre/cover/description from a prior scan of the same ISBN.
    description: z.string().max(10000).optional(),
    genre: z.string().max(120).optional(),
    coverUrl: z.string().url().max(2000).optional(),
    condition: bookConditionSchema.default("good"),
    // Identifier — email OR human name. Server resolves: contains "@" →
    // email path (find or create user keyed by email); otherwise → name
    // path (lookup first/last name; create with synthetic email if missing).
    // Wire field stays `donorEmail` for backwards compat.
    donorEmail: z.string().trim().min(2).max(320).optional(),
    noCredit: z.boolean().default(false),
})
    .refine((d) => !!d.isbn || !!d.title, {
    message: "É preciso ISBN ou título",
    path: ["isbn"],
})
    .refine((d) => d.noCredit || !!d.donorEmail, {
    message: 'Indica email do doador ou marca "Sem créditos"',
    path: ["donorEmail"],
});
// --- holds ---
export const holdReleaseReasonSchema = z.enum(["collected", "expired", "admin_override"]);
export const holdSchema = z.object({
    id: z.string().uuid(),
    bookId: z.string().uuid(),
    bookTitle: z.string(),
    bookAuthor: z.string().nullable(),
    bookCoverUrl: z.string().nullable(),
    createdAt: z.string(),
    expiresAt: z.string(),
    releasedAt: z.string().nullable(),
    releasedReason: holdReleaseReasonSchema.nullable(),
});
// --- appointments ---
export const appointmentTypeSchema = z.enum(["pickup", "dropoff"]);
export const appointmentStatusSchema = z.enum(["confirmed", "cancelled"]);
export const slotSchema = z.object({
    startTime: z.string(),
    endTime: z.string(),
});
export const slotsResponseSchema = z.object({
    slots: z.array(slotSchema),
    timezone: z.string(),
});
export const appointmentSchema = z.object({
    id: z.string().uuid(),
    type: appointmentTypeSchema,
    startTime: z.string(),
    endTime: z.string(),
    holdId: z.string().uuid().nullable(),
    bookTitle: z.string().nullable(),
    status: appointmentStatusSchema,
    notes: z.string().nullable(),
    createdAt: z.string(),
});
export const bookAppointmentSchema = z.object({
    type: appointmentTypeSchema,
    startTime: z.string().datetime(),
    holdId: z.string().uuid().optional(),
    notes: z.string().trim().max(500).optional(),
});
export const walkInRequestSchema = z.object({
    holdId: z.string().uuid(),
});
// --- store status (Volta Já) ---
export const storeStatusSchema = z.object({
    isOpen: z.boolean(),
    message: z.string().nullable(),
    expectedReturnAt: z.string().nullable(),
});
export const storeStatusUpdateSchema = z.object({
    isOpen: z.boolean(),
    message: z.string().trim().max(200).nullable().optional(),
    expectedReturnAt: z.string().datetime().nullable().optional(),
});
// --- store opening hours ---
// "HH:MM" 24h. A day is either an open/close window or null (closed).
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Hora inválida (HH:MM)");
export const dayHoursSchema = z
    .object({ open: hhmm, close: hhmm })
    .refine((d) => d.open < d.close, { message: "A abertura tem de ser antes do fecho" })
    .nullable();
// Exactly 7 entries, Monday-first (index 0 = Monday … 6 = Sunday).
export const storeHoursUpdateSchema = z.object({
    days: z.array(dayHoursSchema).length(7, "São necessários 7 dias (Seg→Dom)"),
});
// Public shape returned by /api/store-hours: the structured week plus a
// pre-rendered human string for simple display.
export const storeHoursPublicSchema = z.object({
    days: z.array(dayHoursSchema).length(7),
    display: z.string(),
});
// --- transactions ---
export const pointReasonSchema = z.enum([
    "signup",
    "donation_scan",
    "checkout",
    "refund_collect",
    "refund_expire",
    "refund_return",
    "admin_adjust",
    "admin_grant",
]);
// Partial update for the admin catalog. All fields optional — only those
// provided are applied. Empty string is treated as "set to null" for
// nullable columns (author, genre, notes).
export const adminBookPatchSchema = z
    .object({
    title: z.string().trim().min(1).max(500).optional(),
    author: z.string().trim().max(500).nullable().optional(),
    genre: z.string().trim().max(120).nullable().optional(),
    condition: bookConditionSchema.optional(),
    notes: z.string().max(2000).nullable().optional(),
    // Raw ISBN as typed by the admin — hyphens/spaces tolerated. The server
    // cleans (cleanIsbnDigits) and validates the 10/13-digit shape, then maps
    // empty → null. max(20) matches the books.isbn column width.
    isbn: z.string().trim().max(20).nullable().optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: "Indica pelo menos um campo a alterar",
});
// Row-level schema for bulk Excel CHECKOUT upload. Identifies an existing
// book (by ISBN preferred, title fallback). userEmail is optional — when
// blank the book leaves the catalogue without anyone being charged (same
// as the explicit noCredit=true override).
export const checkoutUploadRowSchema = z
    .object({
    isbn: z.string().trim().max(32).optional(),
    title: z.string().trim().max(500).optional(),
    // Identifier — email OR human name (resolved server-side via
    // resolveOrCreateUser), mirroring the check-in donorEmail field.
    userEmail: z.string().trim().min(2).max(320).optional(),
    noCredit: z.boolean().default(false),
})
    .refine((d) => !!d.isbn || !!d.title, {
    message: "Indica ISBN ou título para identificar o livro",
    path: ["isbn"],
});
// Row-level schema for bulk Excel/CSV/ODS upload. Title is the only
// REQUIRED column; every other field can be blank. A row with no
// donorEmail (and no explicit noCredit flag) is treated as anonymous —
// book is recorded, no user is created, no point is credited. Matches
// the spreadsheet-editor expectation that "if I leave a cell empty,
// the importer just skips it" rather than rejecting the whole row.
export const bookUploadRowSchema = z.object({
    title: z.string().trim().min(1).max(500),
    author: z.string().trim().max(500).optional(),
    isbn: z.string().trim().max(32).optional(),
    genre: z.string().trim().max(120).optional(),
    condition: bookConditionSchema.default("good"),
    // Identifier — email OR human name (resolved server-side via
    // resolveOrCreateUser). Wire field name kept as donorEmail for
    // backwards compat with existing spreadsheet templates.
    donorEmail: z.string().trim().min(2).max(320).optional(),
    notes: z.string().trim().max(2000).optional(),
    noCredit: z.boolean().default(false),
});
export const adminBookListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    genre: z.string().trim().max(120).optional(),
    author: z.string().trim().max(500).optional(),
    status: bookStatusSchema.optional(),
    includeRemoved: z.coerce.boolean().default(false),
    page: z.coerce.number().int().positive().max(1000).default(1),
    limit: z.coerce.number().int().positive().max(100).default(30),
});
// Admin-only edits to a user record. firstName / lastName are nullable —
// empty string from the client is normalised to null server-side.
export const adminUserPatchSchema = z
    .object({
    firstName: z.string().trim().max(120).nullable().optional(),
    lastName: z.string().trim().max(120).nullable().optional(),
    email: z.string().trim().email().max(320).optional(),
    // Absolute points balance — the server computes the delta vs the current
    // balance and records an admin_grant transaction so the change is audited.
    points: z.number().int().min(0).max(100000).optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: "Indica pelo menos um campo a alterar",
});
// Public data-deletion (RGPD/GDPR right-to-erasure) request. Emailed to the
// admin, who verifies identity and processes the deletion.
export const dataDeletionRequestSchema = z.object({
    email: z.string().trim().email().max(320),
    message: z.string().trim().max(1000).optional(),
});
export const adminCreditSchema = z.object({
    userEmail: z.string().email().max(320),
    delta: z.number().int().min(-1000).max(1000).refine((d) => d !== 0, {
        message: "Delta não pode ser zero",
    }),
    note: z.string().trim().max(200).optional(),
});
export const transactionSchema = z.object({
    id: z.string().uuid(),
    delta: z.number().int(),
    reason: pointReasonSchema,
    bookTitle: z.string().nullable(),
    createdAt: z.string(),
});
